import React, { useState, useEffect, useCallback } from 'react';
import { configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Loader2, Edit, Trash2, DollarSign, Filter } from 'lucide-react';
import { toast } from 'sonner';

const BRANCHES = ['KISAASI', 'BUNGA', 'KIGALI', 'LUBOWA'];
const FEE_HEADS = [
  'tuition', 'club', 'uniform', 'lunch', 'transport', 'examination',
  'id_cards', 'registration', 'capital_levy', 'day_care', 'field_trips',
  'jersey', 'textbooks', 'locker_keys', 'others'
];

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const RateMasterPage = () => {
  const { isAdmin } = useAuth();
  const [rates, setRates] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    branch: 'all',
    class_name: 'all',
    term: 'all',
    academic_year: '2025-2026'
  });

  const [newRate, setNewRate] = useState({
    branch: '',
    class_name: '',
    term: '',
    fee_head: '',
    amount: '',
    currency: 'USD',
    academic_year: '2025-2026'
  });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await configApi.get();
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { academic_year: filters.academic_year };
      if (filters.branch !== 'all') params.branch = filters.branch;
      if (filters.class_name !== 'all') params.class_name = filters.class_name;
      if (filters.term !== 'all') params.term = filters.term;
      
      const response = await api.get('/rate-master', { params });
      setRates(response.data);
    } catch (err) {
      toast.error('Failed to load rates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingRate) {
        await api.put(`/rate-master/${editingRate.id}`, {
          ...newRate,
          amount: parseFloat(newRate.amount)
        });
        toast.success('Rate updated successfully');
      } else {
        await api.post('/rate-master', {
          ...newRate,
          amount: parseFloat(newRate.amount)
        });
        toast.success('Rate created successfully');
      }
      setShowDialog(false);
      setEditingRate(null);
      setNewRate({
        branch: '',
        class_name: '',
        term: '',
        fee_head: '',
        amount: '',
        currency: 'USD',
        academic_year: '2025-2026'
      });
      fetchRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setNewRate({
      branch: rate.branch,
      class_name: rate.class_name,
      term: rate.term,
      fee_head: rate.fee_head,
      amount: rate.amount.toString(),
      currency: rate.currency || 'USD',
      academic_year: rate.academic_year
    });
    setShowDialog(true);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) return;
    try {
      await api.delete(`/rate-master/${rateId}`);
      toast.success('Rate deleted');
      fetchRates();
    } catch (err) {
      toast.error('Failed to delete rate');
    }
  };

  const openNewRateDialog = () => {
    setEditingRate(null);
    setNewRate({
      branch: filters.branch !== 'all' ? filters.branch : '',
      class_name: filters.class_name !== 'all' ? filters.class_name : '',
      term: filters.term !== 'all' ? filters.term : '',
      fee_head: '',
      amount: '',
      currency: 'USD',
      academic_year: filters.academic_year
    });
    setShowDialog(true);
  };

  // Group rates by branch and class
  const groupedRates = rates.reduce((acc, rate) => {
    const key = `${rate.branch}-${rate.class_name}`;
    if (!acc[key]) {
      acc[key] = {
        branch: rate.branch,
        class_name: rate.class_name,
        rates: []
      };
    }
    acc[key].rates.push(rate);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Rate Master</h1>
          <p className="text-[#5C635F] mt-1">Define standard fee rates by branch, class, and term</p>
        </div>
        
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setEditingRate(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={openNewRateDialog} data-testid="add-rate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Rate
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRate ? 'Edit Rate' : 'Add New Rate'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select value={newRate.branch} onValueChange={(v) => setNewRate({ ...newRate, branch: v })}>
                    <SelectTrigger data-testid="rate-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANCHES.map((branch) => (
                        <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Class</Label>
                  <Select value={newRate.class_name} onValueChange={(v) => setNewRate({ ...newRate, class_name: v })}>
                    <SelectTrigger data-testid="rate-class-select">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.classes?.map((cls) => (
                        <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Term</Label>
                  <Select value={newRate.term} onValueChange={(v) => setNewRate({ ...newRate, term: v })}>
                    <SelectTrigger data-testid="rate-term-select">
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.terms?.map((term) => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fee Head</Label>
                  <Select value={newRate.fee_head} onValueChange={(v) => setNewRate({ ...newRate, fee_head: v })}>
                    <SelectTrigger data-testid="rate-feehead-select">
                      <SelectValue placeholder="Select fee head" />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_HEADS.map((head) => (
                        <SelectItem key={head} value={head}>{config?.fee_head_display?.[head] || head.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (USD)</Label>
                  <Input
                    type="number"
                    value={newRate.amount}
                    onChange={(e) => setNewRate({ ...newRate, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    min="0"
                    step="0.01"
                    data-testid="rate-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Academic Year</Label>
                  <Select value={newRate.academic_year} onValueChange={(v) => setNewRate({ ...newRate, academic_year: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button 
                  type="submit" 
                  className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  disabled={submitting || !newRate.branch || !newRate.class_name || !newRate.term || !newRate.fee_head || !newRate.amount}
                  data-testid="submit-rate-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingRate ? 'Update Rate' : 'Create Rate'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex items-center gap-2 text-sm text-[#5C635F]">
              <Filter className="w-4 h-4" />
              Filters:
            </div>
            <Select value={filters.academic_year} onValueChange={(v) => setFilters({ ...filters, academic_year: v })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.branch} onValueChange={(v) => setFilters({ ...filters, branch: v })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {BRANCHES.map((branch) => (
                  <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.class_name} onValueChange={(v) => setFilters({ ...filters, class_name: v })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {config?.classes?.map((cls) => (
                  <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.term} onValueChange={(v) => setFilters({ ...filters, term: v })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Terms</SelectItem>
                {config?.terms?.map((term) => (
                  <SelectItem key={term} value={term}>{term}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Rates Display */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
        </div>
      ) : Object.keys(groupedRates).length === 0 ? (
        <Card className="border-[#D8D9E8]">
          <CardContent className="p-8 text-center text-[#5C635F]">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No rates defined</p>
            <p className="text-sm mt-1">Add rates using the button above</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.values(groupedRates).map((group) => (
            <Card key={`${group.branch}-${group.class_name}`} className="border-[#D8D9E8]">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#3E3F96]/10 text-[#3E3F96]">{group.branch}</Badge>
                  <Badge variant="outline">{group.class_name}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase">Fee Head</TableHead>
                        <TableHead className="text-xs uppercase">Term</TableHead>
                        <TableHead className="text-xs uppercase text-right">Amount</TableHead>
                        <TableHead className="text-xs uppercase">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.rates.sort((a, b) => a.term.localeCompare(b.term)).map((rate) => (
                        <TableRow key={rate.id}>
                          <TableCell className="font-medium">{config?.fee_head_display?.[rate.fee_head] || rate.fee_head?.replace(/_/g, ' ')}</TableCell>
                          <TableCell>{rate.term}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium text-[#3E3F96]">
                            {formatCurrency(rate.amount, rate.currency)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(rate)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleDelete(rate.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RateMasterPage;
